/**
 * People Space usage analytics — the FIRST-PARTY sink.
 *
 * Why this exists alongside Segment and Firebase: the Segment leg needs a write key we don't
 * have, and the Firebase leg lands in a GA4 property shared with the FINN Spaces app. This sink
 * posts the same events to our own proxy, which stores and aggregates them in Firestore and
 * serves the Usage board — so "which features get used and where do people get stuck" is
 * answerable with nothing outside this repo. Segment stays wired for the day its key arrives.
 *
 * ── Shape ────────────────────────────────────────────────────────────────────────────────────
 * Events are buffered and POSTed in small batches rather than one request per event: a recruiter
 * clicking through the board would otherwise fire a dozen requests a minute. The buffer flushes
 * when it reaches FLUSH_AT, on a FLUSH_INTERVAL_MS timer, and when the page is hidden or unloaded
 * (`keepalive` so the last batch survives the navigation).
 *
 * ── PII rule (identical to segment.ts, and enforced here) ────────────────────────────────────
 * NO candidate data may reach analytics — no names, emails, subjects or message text, in event
 * names or properties. Track categories, counts, booleans, durations. The signed-in recruiter is
 * identified by the proxy from the verified Firebase token, never by anything the client sends,
 * so this module transmits no identity at all. As a backstop `sanitizeProps` drops non-primitive
 * values and truncates strings — a property that somehow carried a subject line arrives clipped.
 *
 * ── Why this deliberately does NOT use authenticatedFetch ────────────────────────────────────
 * (CLAUDE.md's rule is "never plain fetch to the proxy"; this is the one considered exception.)
 * `authenticatedFetch` funnels every failure into `reportGlobalError`, which is the same channel
 * that produces `client_error` events. A transport that emits an event whenever it fails would
 * feed itself: failed flush → new event → scheduled flush → failed flush. So this module attaches
 * the Firebase ID token itself, swallows every error, and after MAX_FAILURES consecutive failures
 * gives up for the rest of the session. Analytics must never be the reason a page misbehaves.
 */
import { getFirebaseIdToken } from '../shared/utils/authenticatedFetch';
import { isBlockedHost } from './hostGuard';
import { currentPath, redactPath } from './redactPath';

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-people-520260363689.europe-west3.run.app';

/** Flush when the buffer reaches this many events. */
const FLUSH_AT = 20;
/** …or this long after the first event that landed in an empty buffer. */
const FLUSH_INTERVAL_MS = 5000;
/** Hard cap so a broken flush loop can never grow the buffer without bound. */
const MAX_BUFFER = 200;
/** Consecutive failed flushes after which this sink switches itself off for the session. */
const MAX_FAILURES = 3;
/** Property limits — a backstop against oversized or accidentally sensitive values. */
const MAX_PROPS = 12;
const MAX_STRING_LENGTH = 120;

export interface UsageEventProps {
  [key: string]: string | number | boolean | null | undefined;
}

interface QueuedEvent {
  name: string;
  path: string;
  props: Record<string, string | number | boolean>;
  ts: string;
}

let buffer: QueuedEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let failures = 0;
let listenersInstalled = false;

/** True when this sink may send: a real host (or an explicit local opt-in) and not yet given up. */
export function usageSinkEnabled(): boolean {
  return failures < MAX_FAILURES && !isBlockedHost(import.meta.env.VITE_USAGE_DEBUG);
}

/**
 * Keep only primitives, cap the count, and clip long strings. Event properties are supposed to be
 * categories and counts; anything else is either a mistake or a PII risk, and neither belongs in
 * the store.
 */
function sanitizeProps(props: UsageEventProps): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(props)) {
    if (Object.keys(out).length >= MAX_PROPS) break;
    if (value === null || value === undefined) continue;
    if (typeof value === 'string') {
      out[key] = value.length > MAX_STRING_LENGTH ? value.slice(0, MAX_STRING_LENGTH) : value;
    } else if (typeof value === 'number') {
      if (Number.isFinite(value)) out[key] = value;
    } else if (typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}

/**
 * Flush on hide, not only on unload: `beforeunload`/`unload` are unreliable on mobile and in
 * bfcache-eligible navigations, and `visibilitychange → hidden` is the event that actually fires
 * when someone switches tabs or closes the laptop mid-session.
 */
function installLifecycleListeners(): void {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushUsageEvents();
  });
  window.addEventListener('pagehide', () => void flushUsageEvents());
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flushUsageEvents();
  }, FLUSH_INTERVAL_MS);
}

/** Queue one event. Fire-and-forget: never throws, never awaits the network. */
export function trackUsageEvent(name: string, props: UsageEventProps = {}): void {
  if (!usageSinkEnabled() || !name) return;
  installLifecycleListeners();
  buffer.push({
    name,
    // Redacted: this stamp is applied on EVERY page, including the public `/book/:token` ones
    // where the pathname is the candidate's booking credential.
    path: currentPath(),
    props: sanitizeProps(props),
    ts: new Date().toISOString(),
  });
  // Drop the OLDEST events past the cap: the recent ones describe what the user is doing now.
  if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
  if (buffer.length >= FLUSH_AT) void flushUsageEvents();
  else scheduleFlush();
}

/** SPA route change. Recorded as a normal event so the board can rank views by traffic. */
export function trackUsagePage(path: string): void {
  trackUsageEvent('page_viewed', { path: redactPath(path) });
}

/**
 * Send whatever is buffered. Safe to call at any time and from any trigger.
 *
 * On failure the batch is put BACK at the front of the buffer (so a blip doesn't lose the
 * session's events) and the failure counter advances; MAX_FAILURES consecutive failures — a
 * signed-out user, a proxy outage, a browser blocking the request — retire the sink quietly.
 */
export async function flushUsageEvents(): Promise<void> {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (buffer.length === 0 || !usageSinkEnabled()) return;

  const batch = buffer;
  buffer = [];

  try {
    const token = await getFirebaseIdToken();
    // Signed-out (or pre-auth) events have no owner and are not worth keeping: drop them rather
    // than holding a batch that will never be sendable.
    if (!token) return;

    const response = await fetch(`${PROXY_URL}/api/usage/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ events: batch }),
      keepalive: true,
    });
    if (!response.ok) throw new Error(`usage flush failed: ${response.status}`);
    failures = 0;
  } catch {
    failures += 1;
    if (failures < MAX_FAILURES) {
      buffer = [...batch, ...buffer].slice(-MAX_BUFFER);
      // Re-arm the timer. Restoring the batch is not enough on its own: this function clears
      // `flushTimer` on entry, so without this a transient failure leaves the events sitting in
      // memory until the user happens to act again — and a user who has stopped interacting is
      // exactly the case the restore was written for.
      scheduleFlush();
    }
  }
}

/** @internal test seam */
export function _resetUsageSinkForTests(): void {
  buffer = [];
  failures = 0;
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = null;
  listenersInstalled = false;
}
